import * as Popover from '@radix-ui/react-popover';
import { Avatar } from '@synoptic/ui-kit/avatar/avatar.js';
import { Badge, BadgeProps } from '@synoptic/ui-kit/badge/badge.js';
import { CrownIcon } from '@synoptic/ui-kit/icons/react/crown.js';
import { FireFlameIcon } from '@synoptic/ui-kit/icons/react/fire-flame.js';
import { HeartIcon } from '@synoptic/ui-kit/icons/react/heart.js';
import { MedalIcon } from '@synoptic/ui-kit/icons/react/medal.js';
import { MultiBubbleIcon } from '@synoptic/ui-kit/icons/react/multi-bubble.js';
import { RepostIcon } from '@synoptic/ui-kit/icons/react/repost.js';
import { StatDownIcon } from '@synoptic/ui-kit/icons/react/stat-down.js';
import { StatUpIcon } from '@synoptic/ui-kit/icons/react/stat-up.js';
import { TrophyIcon } from '@synoptic/ui-kit/icons/react/trophy.js';
import { ViewsStatIcon } from '@synoptic/ui-kit/icons/react/views-stat.js';
import { purple, white } from '@synoptic/ui-kit/palette.js';
import { space } from '@synoptic/ui-kit/tokens.js';
import { ellipsis } from '@synoptic/ui-kit/utility.css.js';
import type { Engine, ISourceOptions } from '@tsparticles/engine';
import Particles, { ParticlesProvider } from '@tsparticles/react';
import { loadSlim } from '@tsparticles/slim';
import clsx from 'clsx';
import { useState } from 'react';
import { communityLabels } from '../_app.ct/sigma-graph/community-labels';
import { CardFooter } from './card-footer';
import { CardLink } from './card-link';
import { UserStats } from './user-stats-query';
import {
  card1,
  card13CharacterImage,
  card13Confetti,
  card13Content,
  card13Title,
  card1Header,
  card1HeaderInfo,
  card1HeaderInfoDivider,
  card1HeaderInfoTitle,
  card1InlineStat,
  card1RankRow,
  card1WordsSection,
  card4,
  card4ReachList,
  card5,
  card6,
  card7,
  card7UsersStack,
  cardAvatar,
  cardBadge,
  cardBody,
  cardCircleStatLabel,
  cardCommunityItem,
  cardCommunityList,
  cardCommunityListItem,
  cardContent,
  cardEmoji,
  cardEmojisList,
  cardFlowIndicatorAvatar,
  cardFlowIndicatorAvatarWrapper,
  cardFlowIndicatorContainer,
  cardFlowIndicatorLabel,
  cardFlowIndicatorLabels,
  cardFlowIndicatorLine,
  cardFlowSection,
  cardFlowSectionDescription,
  cardFlowSectionTitle,
  cardHeader,
  cardHeaderDescription,
  cardHeaderInfoLeft,
  cardHeaderName,
  cardHeaderSubTitle,
  cardHeaderTitle,
  cardHeaderUsername,
  cardHeavyweightsFollowers,
  cardHeavyweightsFollowersValue,
  cardHeavyweightsItem,
  cardHeavyweightsItemInfo,
  cardHeavyweightsList,
  cardLargeAvatar,
  cardMutualAvatar,
  cardMutualItem,
  cardMutualScore,
  cardMutualScoreLabel,
  cardMutualScoreValue,
  cardMutualStack,
  cardMutualStackHeader,
  cardMutualUser,
  cardMutualUsers,
  cardPostContent,
  cardPostHeader,
  cardPostItem,
  cardPostName,
  cardPostsList,
  cardPostStatItem,
  cardPostStatItemText,
  cardPostStats,
  cardPostStatsBadge,
  cardPostUsername,
  cardPostUsernameStack,
  cardRankOutOf,
  cardRankSection,
  cardReachCircle,
  cardReachStatValue,
  cardRegularsItem,
  cardRegularsItemDescription,
  cardRegularsItemInfo,
  cardRegularsItemUsername,
  cardStatContainer,
  cardStatLabel,
  cardStatLinkSection,
  cardStatList,
  cardStatValue,
  cardTopCommunityName,
  cardUsername,
  cardWordsList,
  cardWordsSection,
  cardWordsTitle,
} from './cards.css';

const particlesInit = async (engine: Engine) => {
  await loadSlim(engine);
};

const wordColors = [
  'primary',
  'auxLightBlue',
  'auxLightOrange',
  'auxLightTeal',
  'auxLightYellow',
] as const;

type CardProps = {
  userStats: UserStats;
};

const CardHeader = ({
  title,
  subTitle,
  description,
}: {
  title: string;
  subTitle?: string;
  description?: string;
}) => (
  <div className={cardHeader}>
    <div className={cardHeaderTitle}>
      {title}
      {subTitle ? (
        <span className={cardHeaderSubTitle}> {subTitle}</span>
      ) : null}
    </div>

    {description && <div className={cardHeaderDescription}>{description}</div>}
  </div>
);

const CardBody = ({ children, ...props }: React.ComponentProps<'div'>) => (
  <div className={cardBody} {...props}>
    {children}
  </div>
);

const CardBadge = ({
  style,
  children,
  className,
  color = 'primary',
}: {
  style?: React.CSSProperties;
  children: React.ReactNode;
  className?: string;
  color?: BadgeProps['color'];
}) => (
  <Badge
    data-color={color}
    color={color}
    className={clsx(cardBadge, className)}
    style={style}
  >
    {children}
  </Badge>
);

const formatCompactNumber = (number: number) =>
  number.toLocaleString('en-US', {
    maximumFractionDigits: 2,
    notation: 'compact',
    compactDisplay: 'short',
  });

const getTotalInteractions = (tweet: UserStats['top_tweets'][number]) => {
  return (
    tweet.like_count +
    tweet.reply_count +
    tweet.retweet_count +
    tweet.quote_count
  );
};

const inversePercentile = (percentile: number) => {
  const value = 100 - percentile;
  if (value >= 1) {
    return Math.round(value);
  }
  // find number of non-zero decimal places
  const decimalStr = value.toFixed(10).replace(/^-?0\.?/, ''); // remove leading 0.
  const firstNonZeroIdx = decimalStr.search(/[^0]/);
  if (firstNonZeroIdx === -1) {
    // value is exactly 0
    return 0;
  }
  // keep up to first non-zero + 1 digits
  const digitsToKeep = firstNonZeroIdx + 1;
  return parseFloat(value.toFixed(digitsToKeep));
};

const highQualityAvatar = (imageSrc?: string) => {
  return imageSrc?.replace('_normal', '_400x400');
};

export const IntroCard = ({
  userStats,
  noLink = false,
}: {
  noLink?: boolean;
  userStats: Pick<
    UserStats,
    | 'name'
    | 'username'
    | 'icon_url'
    | 'top_percentile'
    | 'rank'
    | 'words'
    | 'communities_percentiles'
    | 'community'
    | 'reach'
    | 'reach_avg'
    | 'top_tweets'
  >;
}) => {
  const reachRatio = userStats.reach / userStats.reach_avg;
  const reachLabel =
    reachRatio > 1 ? (
      <>
        <strong>{Number(reachRatio.toFixed(1))}x</strong>&nbsp;above average
        reach
      </>
    ) : reachRatio < 1 ? (
      <>
        <strong>{Number((1 / reachRatio).toFixed(1))}x</strong>&nbsp;below
        average reach
      </>
    ) : (
      <>
        <strong>1.0x</strong> above average reach
      </>
    );

  const reachIcon =
    reachRatio >= 1 ? (
      <StatUpIcon width={16} height={16} />
    ) : (
      <StatDownIcon width={16} height={16} />
    );

  const loudestHit = userStats.top_tweets.sort(
    (a, b) => getTotalInteractions(b) - getTotalInteractions(a),
  )[0];

  return (
    <div className={cardContent}>
      <CardBody>
        <div className={card1}>
          <div className={card1Header}>
            <Avatar
              size="xl"
              imageSrc={highQualityAvatar(userStats.icon_url)}
              className={cardLargeAvatar}
            />

            <div className={card1HeaderInfo}>
              <div className={card1HeaderInfoTitle}>
                Hey, Main Character,
                <span className={cardHeaderSubTitle}>
                  {' '}
                  you were hard to scroll past.
                </span>
              </div>

              <hr className={card1HeaderInfoDivider} />

              {noLink ? (
                <div className={cardHeaderInfoLeft}>
                  <div className={cardHeaderName}>{userStats.name}</div>
                  <div className={clsx(cardHeaderUsername, cardUsername)}>
                    @{userStats.username}
                  </div>
                </div>
              ) : (
                <CardLink
                  href={`https://x.com/${userStats.username}`}
                  className={cardHeaderInfoLeft}
                >
                  <div className={cardHeaderName}>{userStats.name}</div>
                  <div className={clsx(cardHeaderUsername, cardUsername)}>
                    @{userStats.username}
                  </div>
                </CardLink>
              )}
            </div>
          </div>

          <div className={card1RankRow}>
            <div data-highlight className={cardRankSection}>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: space.s4 }}
                className={cardStatLabel}
              >
                <CrownIcon width={16} height={16} />
                <span>Top percentile</span>
              </div>
              <div className={cardStatValue}>
                Top {inversePercentile(userStats.top_percentile)}%
              </div>
            </div>
            <div className={cardRankSection}>
              <div className={cardStatLabel}>Your rank on CT</div>
              <div>
                <span className={cardStatValue}>
                  {formatCompactNumber(userStats.rank)}{' '}
                </span>
                <span className={cardRankOutOf}>
                  out of {formatCompactNumber(47537)}
                </span>
              </div>
            </div>
          </div>

          <div className={cardRankSection}>
            <div className={card1InlineStat}>
              <TrophyIcon width={16} height={16} />
              <strong>
                Top&nbsp;
                {inversePercentile(
                  userStats.communities_percentiles[userStats.community],
                )}
                %
              </strong>
              &nbsp;in&nbsp;
              <span className={ellipsis}>
                {communityLabels[userStats.community]?.community_label}
              </span>
            </div>
            <div className={card1InlineStat}>
              {reachIcon}
              {reachLabel}
            </div>
            {loudestHit ? (
              <div className={card1InlineStat}>
                <FireFlameIcon width={16} height={16} />
                <strong>
                  {formatCompactNumber(getTotalInteractions(loudestHit))}
                </strong>
                &nbsp;interactions and&nbsp;
                <strong>{formatCompactNumber(loudestHit.view_count)}</strong>
                &nbsp;views on your loudest hit
              </div>
            ) : null}
          </div>

          {userStats.words.length > 0 && (
            <div className={card1WordsSection}>
              <div className={cardStatLabel}>Most used words</div>
              <div className={cardWordsList}>
                {userStats.words.slice(0, 5).map((word, idx) => (
                  <CardBadge
                    key={word}
                    color={wordColors[idx % wordColors.length]}
                  >
                    {word}
                  </CardBadge>
                ))}
              </div>
            </div>
          )}
        </div>
      </CardBody>

      <CardFooter
        username={userStats.username}
        avatarSrc={userStats.icon_url}
      />
    </div>
  );
};

export const CommunitiesCard = ({ userStats }: CardProps) => {
  const communities = Object.entries(userStats.communities_percentiles).map(
    ([community, percentile]) => {
      return (
        <div key={community} className={cardCommunityListItem}>
          <div className={cardCommunityItem}>
            <div className={cardTopCommunityName}>
              {communityLabels[Number(community)]?.community_label}
            </div>
          </div>
          <CardBadge>
            <TrophyIcon />
            Standing: Top {inversePercentile(percentile)}%
          </CardBadge>
        </div>
      );
    },
  );

  const communitiesCount = communities.length;

  return (
    <div className={cardContent}>
      <CardHeader
        title="Your CT Neighborhoods."
        subTitle="you were hard to scroll past."
        description="Standing = your weight in that community’s network graph (cross-follows)."
      />

      <CardBody>
        <div className={cardCommunityList} data-count={communitiesCount}>
          {communities}
        </div>
      </CardBody>
      <CardFooter
        username={userStats.username}
        avatarSrc={userStats.icon_url}
      />
    </div>
  );
};

export const ReachCard = ({ userStats }: CardProps) => (
  <div className={cardContent}>
    <CardHeader
      title="You punch above your weight."
      subTitle="Went wider than usual."
      description="Median (likes + reposts + replies + quotes) per follower, vs the whole graph."
    />
    <CardBody>
      <div className={card4}>
        <div className={card4ReachList}>
          <div
            className={cardReachCircle}
            data-small={userStats.reach_avg < userStats.reach ? '' : undefined}
          >
            <div className={cardCircleStatLabel}>Median</div>
            <div className={cardReachStatValue}>
              {formatCompactNumber(userStats.reach_avg)}
            </div>
          </div>
          <div
            className={cardReachCircle}
            data-small={userStats.reach < userStats.reach_avg ? '' : undefined}
          >
            <div className={cardCircleStatLabel}>Reach</div>
            <div className={cardReachStatValue}>
              {formatCompactNumber(userStats.reach)}
            </div>
          </div>
        </div>

        <div className={cardStatList}>
          <div className={cardStatContainer}>
            <div className={cardStatValue}>
              {formatCompactNumber(userStats.followers)}
            </div>
            <div className={cardStatLabel}>followers</div>
          </div>
          <div className={cardStatContainer}>
            <div className={cardStatValue}>
              Top {inversePercentile(userStats.reach_percentile)}%
            </div>
            <div className={cardStatLabel}>Percentile</div>
          </div>
          <div className={cardStatContainer}>
            <div className={cardStatValue}>
              {formatCompactNumber(userStats.reach_per_post)}x
            </div>
            <div className={cardStatLabel}>per post reach</div>
          </div>
        </div>
      </div>
    </CardBody>
    <CardFooter username={userStats.username} avatarSrc={userStats.icon_url} />
  </div>
);

export const FlowCard = ({ userStats }: CardProps) => {
  const flowRankPercentile = inversePercentile(userStats.flow_rank_percentile);
  const [collisionBoundary, setCollisionBoundary] =
    useState<HTMLDivElement | null>(null);

  let title = "You're in the mainstream.";
  let subTitle =
    "Conversations naturally flow through you. You're a hub in the network.";

  if (flowRankPercentile >= 30 && flowRankPercentile < 70) {
    title = 'Often in the middle of things.';
    subTitle =
      "You're actively engaged, bridging conversations across communities.";
  } else if (flowRankPercentile >= 70) {
    title = 'You operate underground.';
    subTitle =
      'You engage in focused, niche discussions. Quality over broadcast.';
  }

  return (
    <div ref={setCollisionBoundary} className={cardContent}>
      <CardHeader
        title="Where you sit in CT’s follow web."
        description="We look at who follows whom and see whether you link lots of different groups or mostly live in one tight scene."
      />
      <CardBody>
        <div className={card5}>
          <div className={cardFlowIndicatorContainer}>
            <div className={cardFlowIndicatorLine}>
              <Popover.Root open>
                <Popover.Trigger asChild>
                  <div
                    className={cardFlowIndicatorAvatarWrapper}
                    style={{
                      left: `min(max(2em, ${userStats.flow_rank_percentile}%), calc(100% - 2em))`,
                    }}
                  >
                    <Avatar
                      imageSrc={highQualityAvatar(userStats.icon_url)}
                      className={cardFlowIndicatorAvatar}
                    />
                  </div>
                </Popover.Trigger>
                <Popover.Content
                  onKeyDown={(e) => e.stopPropagation()}
                  onOpenAutoFocus={(e) => e.preventDefault()}
                  collisionBoundary={collisionBoundary}
                  side="top"
                  sideOffset={12}
                  collisionPadding={12}
                >
                  <CardBadge>
                    <MedalIcon />
                    Top {flowRankPercentile}% of the feed
                  </CardBadge>
                </Popover.Content>
              </Popover.Root>
            </div>
            <div className={cardFlowIndicatorLabels}>
              <div className={cardFlowIndicatorLabel}>Underground</div>
              <div className={cardFlowIndicatorLabel}>Mainstream</div>
            </div>
          </div>

          <div className={cardFlowSection}>
            <div className={cardFlowSectionTitle}>
              <TrophyIcon width={16} height={16} />
              Top {flowRankPercentile}%: {title}
            </div>
            <p className={cardFlowSectionDescription}>{subTitle}</p>
          </div>
        </div>
      </CardBody>
      <CardFooter
        username={userStats.username}
        avatarSrc={userStats.icon_url}
      />
    </div>
  );
};

export const MutualsCard = ({ userStats }: CardProps) => (
  <div className={cardContent}>
    <CardHeader
      title="Mutual Appreciation Club."
      subTitle="Talk a lot, share a lot, repeat."
      description="Two‑way replies, mentions, and reposts (likes excluded)."
    ></CardHeader>
    <CardBody>
      <div className={card6}>
        <div className={cardMutualStack}>
          {userStats.top_mutual.slice(0, 3).map((mutual) => (
            <div key={mutual.id} className={cardMutualItem}>
              <div className={cardMutualUsers}>
                <CardLink
                  href={`https://x.com/${userStats.username}`}
                  className={cardMutualUser}
                >
                  <Avatar
                    className={cardMutualAvatar}
                    imageSrc={highQualityAvatar(userStats.icon_url)}
                  />
                  <span>@{userStats.username}</span>
                </CardLink>
                <span>↔</span>
                <CardLink
                  href={`https://x.com/${mutual.username}`}
                  className={cardMutualUser}
                >
                  <Avatar
                    className={cardMutualAvatar}
                    imageSrc={highQualityAvatar(mutual.icon_url)}
                  />
                  <span>@{mutual.username}</span>
                </CardLink>
              </div>
              <div className={cardMutualScore}>
                <span className={cardMutualScoreLabel}>Mutual score</span>
                <span className={cardMutualScoreValue}>
                  {Math.round(mutual.mutual_score)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </CardBody>

    <CardFooter username={userStats.username} avatarSrc={userStats.icon_url} />
  </div>
);

export const RegularsCard = ({ userStats }: CardProps) => (
  <div className={cardContent}>
    <CardHeader
      title="Your Regulars /"
      subTitle="Your Go‑Tos."
      description="Ranked by inbound vs outbound interactions (likes excluded)."
    />
    <CardBody>
      <div className={card7}>
        {userStats.top_inbound.length > 0 && (
          <div className={card7UsersStack}>
            <div className={cardMutualStackHeader}>Your Reply Guys</div>
            <div className={cardMutualStack}>
              {userStats.top_inbound.slice(0, 5).map((inbound) => (
                <CardLink
                  noUnderline
                  key={inbound.id}
                  href={`https://x.com/${inbound.username}`}
                  className={cardRegularsItem}
                >
                  <Avatar
                    size="xm"
                    className={cardAvatar}
                    imageSrc={highQualityAvatar(inbound.icon_url)}
                  />
                  <div className={cardRegularsItemInfo}>
                    <div className={cardRegularsItemUsername}>
                      {inbound.username}
                    </div>
                    <div className={cardRegularsItemDescription}>
                      Engaged you {inbound.total_interactions} times
                    </div>
                  </div>
                </CardLink>
              ))}
            </div>
          </div>
        )}
        {userStats.top_outbound.length > 0 && (
          <div className={card7UsersStack}>
            <div className={cardMutualStackHeader}>Who You Reply Guy To</div>
            <div className={cardMutualStack}>
              {userStats.top_outbound.slice(0, 5).map((outbound) => (
                <CardLink
                  noUnderline
                  key={outbound.id}
                  href={`https://x.com/${outbound.username}`}
                  className={cardRegularsItem}
                >
                  <Avatar
                    size="xm"
                    className={cardAvatar}
                    imageSrc={highQualityAvatar(outbound.icon_url)}
                  />
                  <div className={cardRegularsItemInfo}>
                    <div className={cardRegularsItemUsername}>
                      {outbound.username}
                    </div>
                    <div className={cardRegularsItemDescription}>
                      You engaged {outbound.total_interactions} times
                    </div>
                  </div>
                </CardLink>
              ))}
            </div>
          </div>
        )}
      </div>
    </CardBody>
    <CardFooter username={userStats.username} avatarSrc={userStats.icon_url} />
  </div>
);

export const HeavyweightsCard = ({ userStats }: CardProps) => (
  <div className={cardContent}>
    <CardHeader
      title="Heavyweights In The Audience."
      subTitle="Big accounts lurking in your followers."
      description="Snapshot—counts fluctuate."
    />
    <CardBody>
      <div className={cardHeavyweightsList}>
        {userStats.top_followers.slice(0, 4).map((follower) => (
          <CardLink
            noUnderline
            href={`https://x.com/${follower.username}`}
            key={follower.id}
            className={cardHeavyweightsItem}
          >
            <Avatar
              size="xm"
              className={cardAvatar}
              imageSrc={highQualityAvatar(follower.icon_url)}
            />
            <div className={cardHeavyweightsItemInfo}>
              <div className={cardRegularsItemUsername}>{follower.name}</div>
              <div>{follower.username}</div>
            </div>
            <div className={cardHeavyweightsFollowersValue}>
              {formatCompactNumber(follower.followers_count)}
            </div>
            <div className={cardHeavyweightsFollowers}>Followers</div>
          </CardLink>
        ))}
      </div>
    </CardBody>
    <CardFooter username={userStats.username} avatarSrc={userStats.icon_url} />
  </div>
);

export const LoudestHitsCard = ({ userStats }: CardProps) => (
  <div className={cardContent}>
    <CardHeader
      title="Loudest Hits."
      subTitle="These ones had the timeline nodding."
      description="Total interactions = likes + reposts + replies + quotes."
    />
    <CardBody>
      <div className={cardPostsList}>
        {userStats.top_tweets
          .sort((a, b) => getTotalInteractions(b) - getTotalInteractions(a))
          .slice(0, 3)
          .map((tweet, idx) => (
            <CardLink
              noUnderline
              href={`https://x.com/${tweet.username}/status/${tweet.id}`}
              key={tweet.id}
              className={clsx(cardPostItem, cardStatLinkSection)}
            >
              <div className={cardPostHeader}>
                <Avatar
                  className={cardAvatar}
                  size="xm"
                  imageSrc={highQualityAvatar(tweet.icon_url)}
                />
                <div className={cardPostUsernameStack}>
                  <div className={cardPostName}>{tweet.name}</div>
                  <div className={cardPostUsername}>@{tweet.username}</div>
                </div>

                <CardBadge
                  className={cardPostStatsBadge}
                  style={{ marginLeft: 'auto' }}
                >
                  #{idx + 1}
                </CardBadge>
              </div>
              <div className={cardPostContent}>{tweet.text}</div>
              <div className={cardPostStats}>
                <div className={cardPostStatItem}>
                  <MultiBubbleIcon />
                  <span className={cardPostStatItemText}>
                    {formatCompactNumber(tweet.reply_count)} replies
                  </span>
                </div>
                <div className={cardPostStatItem}>
                  <RepostIcon />
                  <span className={cardPostStatItemText}>
                    {formatCompactNumber(tweet.retweet_count)} retweets
                  </span>
                </div>
                <div className={cardPostStatItem}>
                  <HeartIcon />
                  <span className={cardPostStatItemText}>
                    {formatCompactNumber(tweet.like_count)} likes
                  </span>
                </div>
                <div className={cardPostStatItem}>
                  <ViewsStatIcon />
                  <span className={cardPostStatItemText}>
                    {formatCompactNumber(tweet.view_count)} views
                  </span>
                </div>
              </div>
            </CardLink>
          ))}
      </div>
    </CardBody>

    <CardFooter username={userStats.username} avatarSrc={userStats.icon_url} />
  </div>
);

export const WordsCard = ({ userStats }: CardProps) => (
  <div className={cardContent}>
    <CardHeader
      title="Your lexicon."
      subTitle="Your feed in five words."
      description="Top tokens & emoji by frequency (stopwords removed)."
    />
    <CardBody>
      {userStats.words.length > 0 && (
        <div className={cardWordsSection}>
          <div className={cardWordsTitle}>Most used words</div>
          <div className={cardWordsList}>
            {userStats.words.map((word, idx) => (
              <CardBadge key={word} color={wordColors[idx % wordColors.length]}>
                {word}
              </CardBadge>
            ))}
          </div>
        </div>
      )}
      {userStats.emojis.length > 0 && (
        <div style={{ marginTop: 16 }} className={cardWordsSection}>
          <div className={cardWordsTitle}>Top emojis</div>
          <div className={cardEmojisList}>
            {userStats.emojis.map((emoji) => (
              <span key={emoji} className={cardEmoji}>
                {emoji}
              </span>
            ))}
          </div>
        </div>
      )}
    </CardBody>
    <CardFooter username={userStats.username} avatarSrc={userStats.icon_url} />
  </div>
);

const confettiOptions: ISourceOptions = {
  particles: {
    color: {
      value: [white[500], purple[500], 'rgba(199, 184, 238, 0.50)'],
    },
    move: {
      direction: 'bottom',
      enable: true,
      outModes: {
        default: 'out',
      },
      size: true,
      speed: {
        min: 1,
        max: 3,
      },
    },
    number: {
      value: 800,

      density: {
        enable: true,
      },
    },
    opacity: {
      value: 1,
      animation: {
        enable: false,
        startValue: 'max',
        destroy: 'min',
        speed: 0.3,
        sync: true,
      },
    },
    rotate: {
      value: {
        min: 0,
        max: 360,
      },
      direction: 'random',
      move: true,
      animation: {
        enable: true,
        speed: 30,
      },
    },
    tilt: {
      direction: 'random',
      enable: true,
      move: true,
      value: {
        min: 0,
        max: 360,
      },
      animation: {
        enable: true,
        speed: 60,
      },
    },
    shape: {
      type: ['square', 'triangle'],
    },
    size: {
      value: {
        min: 2,
        max: 10,
      },
    },
    roll: {
      darken: {
        enable: true,
        value: 30,
      },
      enlighten: {
        enable: true,
        value: 30,
      },
      enable: true,
      speed: {
        min: 15,
        max: 25,
      },
    },
    wobble: {
      distance: 30,
      enable: true,
      move: true,
      speed: {
        min: -15,
        max: 15,
      },
    },
  },
};

const Confetti = () => {
  return (
    <ParticlesProvider init={particlesInit}>
      <Particles
        id="tsparticles"
        options={confettiOptions}
        className={card13Confetti}
      />
    </ParticlesProvider>
  );
};

export const FinalCard = ({ userStats }: { userStats: UserStats }) => (
  <div className={cardContent}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="0"
      height="0"
      style={{ position: 'absolute' }}
    >
      <defs>
        <filter
          id="filter0_g_33656_10908"
          x="-9"
          y="-9"
          width="778"
          height="739"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feTurbulence
            type="fractalNoise"
            baseFrequency="5 5"
            numOctaves="3"
            seed="3817"
          />
          <feDisplacementMap
            in="shape"
            scale="18"
            xChannelSelector="R"
            yChannelSelector="G"
            result="displacedImage"
            width="100%"
            height="100%"
          />
          <feMerge result="effect1_texture_33656_10908">
            <feMergeNode in="displacedImage" />
          </feMerge>
        </filter>
      </defs>
    </svg>
    <Confetti />
    <CardBody>
      <div className={card13Content}>
        <h2 className={card13Title}>That was your story!</h2>
        <Avatar
          imageSrc={highQualityAvatar(userStats.icon_url)}
          className={card13CharacterImage}
        />
      </div>
    </CardBody>

    <CardFooter username={userStats.username} avatarSrc={userStats.icon_url} />
  </div>
);
